import { useState, useRef, useEffect } from "react";
import Button from "@/components/ui/Button";
import { Plus, X } from "lucide-react";

interface ItemEntry {
  name: string;
  description?: string;
}

interface ItemListEditorProps {
  items: ItemEntry[];
  onChange: (items: ItemEntry[]) => void;
  mode?: "review" | "custom";
}

export default function ItemListEditor({
  items,
  onChange,
  mode,
}: ItemListEditorProps) {
  const [newItem, setNewItem] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode === "custom" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mode]);

  const handleAdd = () => {
    const name = newItem.trim();
    if (!name || items.length >= 50) return;
    onChange([...items, { name }]);
    setNewItem("");
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  const isCustom = mode === "custom";

  return (
    <div>
      {isCustom ? (
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type and press Enter..."
            maxLength={100}
            className="flex-1 rounded-xl border border-border bg-surface px-4 py-3 text-base text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-border-focus"
          />
          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={!newItem.trim() || items.length >= 50}
            className="shrink-0 px-3"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Add an item..."
            maxLength={100}
            className="flex-1 rounded-xl border border-border bg-surface px-4 py-3 text-base text-text placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-border-focus"
          />
          <Button
            variant="primary"
            onClick={handleAdd}
            disabled={!newItem.trim() || items.length >= 50}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>
      )}
      <p className="mt-1 text-sm text-text-secondary">
        {items.length}/50 items
      </p>
      {items.length > 0 && (
        <ul className="mt-3 space-y-1">
          {items.map((item, i) => (
            <li
              key={i}
              className="flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-3"
            >
              <span className="min-w-0 flex-1 text-base text-text">
                {item.name}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(i)}
                className="shrink-0 rounded-lg p-1 text-text-secondary transition-colors hover:bg-error/10 hover:text-error"
                aria-label={`Remove ${item.name}`}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
